import { useTenantAvailable } from '@api/application-configuration';
import { useLogoutMutation } from '@api/auth';
import { LogOut } from '@tamagui/lucide-icons';
import { Avatar } from '@unistocks/components/avatar/avatar';
import { Group } from '@unistocks/components/group/group';
import { Spinner } from '@unistocks/components/loading/spinner/spinner';
import { ThemeSwitch } from '@unistocks/components/settings/theme-switch';
import { Sheet } from '@unistocks/components/sheet/sheet';
import { RouteListItem } from '@unistocks/features/navigation/route-list-item';
import { useSecondaryRoutes } from '@unistocks/features/navigation/use-routes';
import { useProfileGetQuery } from '@unistocks/proxy/axios-client/ProfileQuery';
import { useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { t } from '@unistocks/shared/localization/index.ts';
import { selectAdminToken } from '@unistocks/shared/store/root.slice';
import { Href, Link } from 'expo-router';
import { Adapt, AvatarProps, ListItem, Popover, Separator } from 'tamagui';

export const ProfileMenu: React.FC<AvatarProps> = props => {
  const secondaryRoutes = useSecondaryRoutes();
  const { data: profile, isLoading, isSuccess } = useProfileGetQuery();
  const { mutate: logout, isPending } = useLogoutMutation();
  const adminToken = useAppSelector(selectAdminToken);
  const isImpersonating = !!adminToken;
  const isTenantUser = useTenantAvailable();

  const editProfileHref: Href = isTenantUser
    ? '/(tabs)/(menu)/settings/edit-profile'
    : '/(admin)/(menu)/settings/edit-profile';

  if (isLoading) {
    return <Spinner />;
  }

  if (isSuccess) {
    return (
      <Popover stayInFrame placement="bottom-end" size={'$5'} offset={18}>
        <Popover.Trigger asChild>
          <Avatar size="$4" {...props} />
        </Popover.Trigger>
        <Popover.Content
          elevation={'$1'}
          borderWidth={1}
          p={0}
          borderColor="$borderColor"
          minW={'$20'}
          transition={'quickLessBouncy'}
          enterStyle={{ y: -10, opacity: 0 }}
          exitStyle={{ y: -10, opacity: 0 }}
        >
          <Popover.Arrow borderWidth={1} />
          <Group render="ul" separator={<Separator />} w={'100%'}>
            <Popover.Close asChild>
              <Link href={editProfileHref} style={{ overflow: 'hidden' }}>
                <Group.Item>
                  <ListItem
                    cursor="pointer"
                    size={'$5'}
                    gap={'$3'}
                    px={'$4'}
                    borderRadius={'$4'}
                    icon={<Avatar size={'$4'} />}
                    title={`${profile.name} ${profile.surname}`}
                    subTitle={profile.email}
                    $md={{
                      size: '$4'
                    }}
                  />
                </Group.Item>
              </Link>
            </Popover.Close>
            {secondaryRoutes.map(route => (
              <Popover.Close asChild key={route.key}>
                <Link href={route.href}>
                  <Group.Item>
                    <RouteListItem
                      bg={undefined}
                      size={'$5'}
                      borderRadius={'$4'}
                      scaleIcon={1.5}
                      route={route}
                      $md={{
                        size: '$4'
                      }}
                    />
                  </Group.Item>
                </Link>
              </Popover.Close>
            ))}
            <Group.Item>
              <ListItem
                size={'$5'}
                borderRadius={'$4'}
                $md={{
                  size: '$4'
                }}
              >
                <ThemeSwitch size={'$2'} gap={'$4'} />
              </ListItem>
            </Group.Item>
            <Popover.Close asChild>
              <Group.Item>
                <ListItem
                  cursor="pointer"
                  disabled={isPending}
                  size={'$5'}
                  gap={'$3'}
                  borderRadius={'$4'}
                  scaleIcon={1.5}
                  borderBottomLeftRadius={'$4'}
                  borderBottomRightRadius={'$4'}
                  icon={LogOut}
                  title={isImpersonating ? t('::Impersonation:ReturnToYourAccount') : t('::Logout')}
                  onPress={() => logout()}
                  $md={{
                    size: '$4'
                  }}
                />
              </Group.Item>
            </Popover.Close>
          </Group>
        </Popover.Content>

        <Adapt when="max-md">
          <Sheet>
            <Adapt.Contents />
          </Sheet>
        </Adapt>
      </Popover>
    );
  }

  return null;
};

ProfileMenu.displayName = 'ProfileMenu';
